import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Loader2 } from 'lucide-react';
import { FilePdf, FileText, Download, Eye, Trash, Sparkle, UploadSimple, PencilLine, CheckCircle } from '@phosphor-icons/react';
import { SignaturePad } from './SignaturePad';
import {
  listFormationTemplates,
  generateFormationDoc,
  getDocuments,
  deleteDocument,
  downloadDocument,
  viewDocument,
  uploadDocument,
  signDocument,
} from '../services/api';

export default function FormationDocsPanel({ dossierId }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [signingDoc, setSigningDoc] = useState(null);

  const fetchDocs = useCallback(async () => {
    if (!dossierId) return;
    try {
      const res = await getDocuments({ dossier_id: dossierId });
      setDocuments(res.data.items || []);
    } catch {
      toast.error('Erreur chargement documents');
    }
  }, [dossierId]);

  const fetchTemplates = useCallback(async () => {
    if (!dossierId) return;
    setLoading(true);
    try {
      const res = await listFormationTemplates(dossierId);
      setTemplates(res.data.items || []);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [dossierId]);

  useEffect(() => {
    fetchTemplates();
    fetchDocs();
  }, [fetchTemplates, fetchDocs]);

  const handleGenerate = async () => {
    if (!selectedTemplate) {
      toast.warning('Choisir un modèle');
      return;
    }
    setGenerating(true);
    try {
      await generateFormationDoc(dossierId, selectedTemplate, {});
      toast.success('Document généré');
      setSelectedTemplate('');
      await fetchDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleView = async (doc) => {
    try {
      const res = await viewDocument(doc.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: doc.content_type || 'application/octet-stream' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Erreur ouverture');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await downloadDocument(doc.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: doc.content_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || `${doc.titre}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur téléchargement');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer le document "${doc.titre}" ?`)) return;
    try {
      await deleteDocument(doc.id);
      toast.success('Document supprimé');
      fetchDocs();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const handleUpload = async (file, replaceOf = null) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('titre', replaceOf ? `${replaceOf.titre} (mis à jour)` : file.name);
      fd.append('type_document', replaceOf?.type_document || 'formation_upload');
      fd.append('dossier_id', dossierId);
      await uploadDocument(fd);
      if (replaceOf) {
        // On archive l'ancien
        await deleteDocument(replaceOf.id);
      }
      toast.success(replaceOf ? 'Document remplacé' : 'Document ajouté');
      fetchDocs();
    } catch {
      toast.error('Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSign = async (signatureDataUrl) => {
    if (!signingDoc) return;
    try {
      await signDocument(signingDoc.id, { signature: signatureDataUrl });
      toast.success('Document signé électroniquement');
      setSigningDoc(null);
      fetchDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur signature');
    }
  };

  return (
    <div className="border-t border-[#E8E6E1] pt-4 space-y-4" data-testid="formation-docs-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
          <Sparkle size={18} className="text-[#2D5A4C]" weight="fill" />
          Documents projets (Formation)
        </h3>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-[#5E6D66]" />}
      </div>

      {templates.length > 0 ? (
        <div className="flex flex-wrap gap-2 items-center bg-[#F9F8F5] p-3 rounded-md border border-[#E8E6E1]">
          <div className="flex-1 min-w-[240px]">
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger data-testid="formation-template-select" className="border-[#E8E6E1]">
                <SelectValue placeholder="Choisir un modèle à générer" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid="generate-formation-doc-btn"
            className="bg-[#2D5A4C] hover:bg-[#1E4136]"
            onClick={handleGenerate}
            disabled={!selectedTemplate || generating}
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FilePdf size={16} className="mr-2" />}
            Générer
          </Button>
          <label className="inline-flex">
            <input
              type="file"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="upload-formation-doc-input"
            />
            <Button
              type="button"
              variant="outline"
              disabled={uploading}
              className="border-[#E8E6E1]"
              onClick={(e) => e.currentTarget.previousSibling.click()}
              data-testid="upload-formation-doc-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UploadSimple size={16} className="mr-2" />}
              Importer
            </Button>
          </label>
        </div>
      ) : (
        <p className="text-xs text-[#5E6D66]">Aucun modèle disponible pour ce type de dossier.</p>
      )}

      <div className="space-y-2" data-testid="formation-docs-list">
        {documents.length === 0 ? (
          <p className="text-xs text-[#5E6D66]">Aucun document généré pour ce dossier.</p>
        ) : (
          documents.map((d) => (
            <div
              key={d.id}
              className="flex items-center gap-3 p-3 bg-white rounded-md border border-[#E8E6E1] hover:border-[#2D5A4C]/40 transition-colors"
              data-testid={`formation-doc-${d.id}`}
            >
              <FileText size={22} className="text-[#2D5A4C] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A2421] truncate">{d.titre}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {d.generated_from_template && (
                    <Badge variant="outline" className="text-[10px]">Généré</Badge>
                  )}
                  <span className="text-[10px] text-[#5E6D66]">
                    {d.created_at ? new Date(d.created_at).toLocaleString('fr-FR') : ''}
                  </span>
                  <span className="text-[10px] text-[#5E6D66]">{Math.round((d.size || 0) / 1024)} Ko</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" title="Aperçu" onClick={() => handleView(d)} data-testid={`view-formation-doc-${d.id}`}>
                  <Eye size={16} />
                </Button>
                <Button size="icon" variant="ghost" title="Télécharger" onClick={() => handleDownload(d)} data-testid={`download-formation-doc-${d.id}`}>
                  <Download size={16} />
                </Button>
                <label>
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => handleUpload(e.target.files?.[0], d)}
                    data-testid={`replace-formation-doc-input-${d.id}`}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    title="Modifier (remplacer)"
                    type="button"
                    onClick={(e) => e.currentTarget.previousSibling.click()}
                    data-testid={`replace-formation-doc-${d.id}`}
                  >
                    <UploadSimple size={16} />
                  </Button>
                </label>
                <Button size="icon" variant="ghost" title="Signer électroniquement" onClick={() => setSigningDoc(d)} data-testid={`sign-formation-doc-${d.id}`}>
                  <PencilLine size={16} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  title="Supprimer"
                  onClick={() => handleDelete(d)}
                  className="text-red-600 hover:text-red-700"
                  data-testid={`delete-formation-doc-${d.id}`}
                >
                  <Trash size={16} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {signingDoc && (
        <div className="border-2 border-[#2D5A4C] rounded-md p-4 bg-[#F9F8F5]" data-testid="signing-panel">
          <p className="text-sm font-semibold mb-2">Signer : {signingDoc.titre}</p>
          <SignaturePad onSave={handleSign} onCancel={() => setSigningDoc(null)} />
        </div>
      )}

      {documents.some((d) => (d.signatures || []).length > 0) && (
        <div className="pt-3 border-t border-[#E8E6E1]">
          <p className="text-xs font-semibold text-[#5E6D66] uppercase tracking-wider mb-2">Signatures apposées</p>
          {documents.filter((d) => (d.signatures || []).length > 0).map((d) => (
            <div key={d.id} className="mb-2">
              <p className="text-[10px] text-[#5E6D66]">{d.titre}</p>
              {(d.signatures || []).map((sig, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-md border border-emerald-200 bg-emerald-50 mt-1" data-testid={`signature-${d.id}-${i}`}>
                  <CheckCircle size={14} className="text-emerald-600" weight="fill" />
                  <span className="text-xs">{sig.signer_name} · {sig.signed_at ? new Date(sig.signed_at).toLocaleString('fr-FR') : ''}</span>
                  <img src={sig.signature} alt="signature" className="h-6 ml-auto opacity-80" />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
